import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Typography, Paper, IconButton } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const DetectionImageViewer = ({
  imageUrl: propImageUrl,
  confidence: propConfidence,
  result: propResult,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
}) => {
  const [searchParams] = useSearchParams();
  const [imageError, setImageError] = useState(false);

  // Use props if provided, otherwise fall back to URL search params
  const imageUrl = propImageUrl || searchParams.get("imageUrl");
  const confidence = propConfidence || searchParams.get("confidence") || "-";
  const result = (
    propResult ||
    searchParams.get("result") ||
    "-"
  ).toLowerCase();

  // Check if component is being used with props (in dialog) or standalone
  const isInDialog = propImageUrl !== undefined;

  // Get result color
  const getResultColor = () => {
    if (result === "real") return "#10b981";
    if (result === "fake") return "#ef4444";
    return "#64748b";
  };

  return (
    <Box
      sx={{
        minHeight: isInDialog ? "auto" : "100vh",
        background: isInDialog ? "transparent" : "#0a0e29",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isInDialog ? "12px" : "20px 12px",
        position: "relative",
      }}
    >
      {/* Left Arrow */}
      {isInDialog && hasPrevious && (
        <IconButton
          onClick={onPrevious}
          sx={{
            position: "absolute",
            left: { xs: 4, sm: 16 },
            top: "50%",
            transform: "translateY(-50%)",
            border: "1px solid #28ABE2",
            backgroundColor: "#fff",
            color: "#28ABE2",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            "&:hover": {
              opacity: 0.7,
            },
            zIndex: 10,
          }}
        >
          <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}

      {/* Right Arrow */}
      {isInDialog && hasNext && (
        <IconButton
          onClick={onNext}
          sx={{
            position: "absolute",
            right: { xs: 4, sm: 16 },
            top: "50%",
            transform: "translateY(-50%)",
            border: "1px solid #28ABE2",
            backgroundColor: "#fff",
            color: "#28ABE2",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            "&:hover": {
              opacity: 0.7,
            },
            zIndex: 10,
          }}
        >
          <ArrowForwardIosIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}

      <Box sx={{ maxWidth: "350px", width: "100%" }}>
        <Paper
          elevation={isInDialog ? 0 : 3}
          sx={{
            borderRadius: "8px",
            overflow: "hidden",
            background: "#ffffff",
          }}
        >
          {/* Image Section */}
          <Box
            sx={{
              padding: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "200px",
            }}
          >
            {imageUrl && !imageError ? (
              <Box
                component="img"
                src={imageUrl}
                alt="Detection result"
                onError={() => setImageError(true)}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "240px",
                  borderRadius: "6px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                  objectFit: "contain",
                }}
              />
            ) : (
              <Typography
                sx={{
                  color: "#94a3b8",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                {imageError
                  ? "Unable to load image"
                  : imageUrl
                  ? "Loading image…"
                  : "No image available"}
              </Typography>
            )}
          </Box>

          {/* Info Section */}
          <Box sx={{ padding: "14px", paddingTop: "6px" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "6px",
                mb: 1.2,
              }}
            >
              {/* Confidence Score */}
              <Box
                sx={{
                  padding: "8px 10px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "9px",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                    color: "#64748b",
                    fontWeight: 600,
                    mb: 0.2,
                  }}
                >
                  Confidence Score
                </Typography>
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: 700,
                    color: "#1e293b",
                  }}
                >
                  {confidence}
                </Typography>
              </Box>

              {/* Prediction */}
              <Box
                sx={{
                  padding: "8px 10px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "9px",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                    color: "#64748b",
                    fontWeight: 600,
                    mb: 0.2,
                  }}
                >
                  Prediction
                </Typography>
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: 700,
                    color: getResultColor(),
                    textTransform: "capitalize",
                  }}
                >
                  {result}
                </Typography>
              </Box>
            </Box>

            {/* Action Button */}
            {imageUrl && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="a"
                  href={imageUrl}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    display: "inline-block",
                    padding: "8px 20px",
                    background: "#28ABE2",
                    color: "#fff",
                    textDecoration: "none",
                    textAlign: "center",
                    borderRadius: "6px",
                    fontWeight: 600,
                    fontSize: "12px",
                    transition: "background 0.2s ease",
                    "&:hover": {
                      background: "#28ABE2",
                      opacity: 0.9,
                    },
                  }}
                >
                  View Source Image
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default DetectionImageViewer;
